import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — N Canal Blvd", address: "1309 N Canal Blvd, Thibodaux, LA 70301", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — N Canal Blvd", address: "400 N Canal Blvd, Thibodaux, LA 70301", hours: "Call for hours" },
  { name: "CVS Pharmacy — Houma", address: "1532 W Main St, Houma, LA 70360", hours: "Daily 7 AM – 11 PM" },
  { name: "The UPS Store — Thibodaux", address: "315 N Canal Blvd, Thibodaux, LA 70301", hours: "Call for hours" },
  { name: "Walgreens — Houma", address: "1824 S Van Ave, Houma, LA 70363", hours: "Call for hours" },
  { name: "CVS Pharmacy — Raceland", address: "4540 Highway 1, Raceland, LA 70394", hours: "Daily 7 AM – 11 PM" },
];

const dmvLocations = [
  {
    name: "Lafourche Parish Clerk of Court — Thibodaux",
    address: "303 W 3rd St, Thibodaux, LA 70301",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "Driver licenses, REAL ID, vehicle registration for Thibodaux and Lafourche Parish. Call ahead for current wait times.",
    featured: true,
  },
  {
    name: "Terrebonne Parish Clerk of Court — Houma",
    address: "7856 Main St, Houma, LA 70360",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "Houma area DMV services. Appointment recommended for REAL ID processing.",
    featured: false,
  },
];

export default function ThibodauxHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Thibodaux Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local bayou region resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Thibodaux</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Thibodaux include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && photoLocations.length > 4 && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Lafourche &amp; Terrebonne Parish DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Louisiana driver&apos;s license or REAL ID, the Lafourche and Terrebonne Parish Clerks of Court handle DMV services in the Thibodaux area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Louisiana Birth, Death, Marriage &amp; Divorce Records</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for your passport application — or need to update other vital records — the Louisiana Department of Health Vital Records Registry handles all statewide vital records requests. Lafourche Parish-specific records can also be obtained through the Lafourche Parish Clerk of Court.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    title: "Louisiana Vital Records Registry",
                    description: "Order certified copies of Louisiana birth and death certificates online, by mail, or in person at the state office in New Orleans.",
                    url: "https://ldh.la.gov/page/vital-records",
                    label: "LA Vital Records Registry",
                    featured: true,
                  },
                  {
                    title: "VitalChek \u2014 Louisiana Birth &amp; Death Certificates",
                    description: "Third-party expedited service for Louisiana birth and death certificates. Faster processing for an additional fee.",
                    url: "https://www.vitalchek.com/",
                    label: "Order Online via VitalChek",
                    featured: false,
                  },
                ].map((resource, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${resource.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{resource.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-3">{resource.description}</p>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{resource.label}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Marriage and divorce records</strong> are maintained by the Lafourche Parish Clerk of Court, not the state vital records office. Contact the parish clerk directly for certified copies of marriage licenses and divorce decrees issued in Lafourche Parish.
                </p>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3 mt-4">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-[#F6C344] mr-1.5"></i>
                  <strong>Passport tip:</strong> Your birth certificate must be a certified copy with a raised seal, stamp, or registrar&apos;s signature. Photocopies and hospital-issued certificates are not accepted by the U.S. Department of State.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Thibodaux Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Thibodaux does not have a commercial airport. Most Thibodaux travelers use New Orleans Louis Armstrong International Airport (MSY). Make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "MSY",
                    name: "New Orleans Louis Armstrong International Airport",
                    distance: "About 50 miles northeast of Thibodaux via US-90",
                    address: "1 Terminal Dr, Kenner, LA 70062",
                    note: "Primary commercial airport for the bayou region. Nonstop service to major domestic and international hubs.",
                    flightUrl: "https://flymsy.com/",
                    flightLabel: "MSY Airport Website",
                    featured: true,
                  },
                  {
                    code: "HUM",
                    name: "Houma-Terrebonne Airport",
                    distance: "About 20 miles south of Thibodaux",
                    address: "Harry P. Williams Memorial Airport, Patterson, LA",
                    note: "General aviation airport. Charter and private aviation services available.",
                    flightUrl: "https://www.houmaairport.com/",
                    flightLabel: "Houma Airport",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}